import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Check, ChevronRight, Globe, Info, RefreshCw, Wifi } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router";

import { Container } from "../components/Container";
import { SEO } from "../components/SEO";
import {
  fetchEsimPackageById,
  type EsimPackage,
} from "../lib/api";
import {
  cardClass,
  errorBoxClass,
  formatDataLabel,
  formatEsimPrice,
  localizedCountryLabel,
  localizedGeographyLabel,
  localizedRegionLabel,
  panelClass,
} from "../lib/esimCatalog";
import {
  flagUrl,
  getCoveredCountries,
  getFairUsageBody,
  getFairUsageTitle,
  getLocalizedPackageDisplayTitle,
  getUnlimitedDataLabel,
  uniqueCoverageLabel,
} from "../lib/esimFairUsage";
import { useLocale } from "../hooks/useLocale";

export function EsimPackageDetails() {
  const { t } = useTranslation();
  const { pathFor, language } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const packageId = params.get("packageId") ?? "";

  const initialPackage = (location.state as { package?: EsimPackage } | null)
    ?.package;
  const [pkg, setPkg] = useState<EsimPackage | null>(initialPackage ?? null);
  const [error, setError] = useState<string | null>(null);
  const [countriesOpen, setCountriesOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    if (pkg || !packageId) return;
    let cancelled = false;
    (async () => {
      try {
        const match = await fetchEsimPackageById(packageId);
        if (cancelled) return;
        if (!match) {
          setError(t("esim.load_package_error"));
          return;
        }
        setPkg(match);
      } catch {
        if (!cancelled) setError(t("esim.load_package_error"));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [packageId, pkg, t]);

  const coveredCountries = useMemo(() => {
    if (!pkg) return [];
    return getCoveredCountries(pkg).map((c) => ({
      ...c,
      name: localizedCountryLabel(c.code, c.name, {
        locale: language.acceptLanguageCode,
        t,
        geography: "local",
      }),
    }));
  }, [pkg, language.acceptLanguageCode, t]);

  const coverageLabel = useMemo(() => {
    if (!pkg) return "";
    const place = localizedCountryLabel(pkg.countryCode, pkg.countryName, {
      locale: language.acceptLanguageCode,
      t,
      geography: pkg.geography,
    });
    const region = localizedRegionLabel(pkg.region, t);
    const geography = localizedGeographyLabel(pkg.geography, t);
    return uniqueCoverageLabel(place, region, geography);
  }, [pkg, language.acceptLanguageCode, t]);

  const previewFlags = coveredCountries.slice(0, 5);
  const extraCount = Math.max(0, coveredCountries.length - previewFlags.length);

  const dataLabel = pkg
    ? pkg.isUnlimited
      ? getUnlimitedDataLabel(pkg, t)
      : formatDataLabel(pkg)
    : "";

  const displayTitle = pkg
    ? getLocalizedPackageDisplayTitle(pkg, t)
    : t("esim.details_title");

  const networksLabel =
    pkg?.networks && pkg.networks.length > 0
      ? pkg.networks.join(" / ")
      : t("esim.data_type_value");

  const activationLabel =
    pkg?.activation === "first-use"
      ? t("esim.activation_first_use")
      : pkg?.activation || "—";

  const goCheckout = () => {
    if (!pkg) return;
    navigate(
      pathFor(
        `/esim/checkout?packageId=${encodeURIComponent(pkg.packageId)}`,
      ),
      { state: { package: pkg } },
    );
  };

  const benefitItems = pkg
    ? [
        t("esim.benefit_qr"),
        t("esim.benefit_switch"),
        t("esim.benefit_secure"),
        t("esim.benefit_multiprofile"),
        ...(pkg.withHotspot ? [t("esim.benefit_hotspot")] : []),
        ...(pkg.withDataRoaming ? [t("esim.benefit_roaming")] : []),
        ...(pkg.withTopUp ? [t("esim.benefit_topup")] : []),
      ]
    : [];

  return (
    <main className="min-h-screen bg-bg-base pt-28 sm:pt-32">
      <SEO
        title={t("esim.meta_title_details")}
        description={t("esim.meta_description_details")}
        path="/esim/package"
      />

      <Container size="md">
        <article className="py-12 sm:py-16">
          <Link
            to={pathFor("/esim/shop")}
            className="text-sm font-semibold text-brand-500"
          >
            {t("esim.back_to_plans")}
          </Link>

          <header className="mt-6 max-w-2xl">
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.25em] text-brand-500">
              {t("esim.details_eyebrow")}
            </p>
            <h1 className="hero-headline text-text-heading">
              {pkg ? displayTitle : t("esim.details_title")}
            </h1>
          </header>

          {error ? <p className={`mt-8 ${errorBoxClass}`}>{error}</p> : null}

          {!pkg && !error && packageId ? (
            <p className="mt-8 text-text-muted">{t("esim.loading_package")}</p>
          ) : null}

          {!pkg && !error && !packageId ? (
            <p className="mt-8 text-text-muted">
              {t("esim.select_package_first")}
            </p>
          ) : null}

          {pkg ? (
            <div className="mt-8 space-y-5">
              <div className="overflow-hidden rounded-2xl bg-gradient-to-br from-brand-500 via-[#C41850] to-[#A01442] p-6 text-white shadow-md">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold uppercase tracking-wider text-white/75">
                      {t("esim.data_label")}
                    </p>
                    <p
                      className={`mt-1 font-bold leading-snug ${
                        pkg.isUnlimited ? "text-xl sm:text-2xl" : "text-3xl"
                      }`}
                    >
                      {dataLabel}
                    </p>
                  </div>
                  <div className="text-end">
                    <p className="text-xs font-bold uppercase tracking-wider text-white/75">
                      {t("esim.price_label")}
                    </p>
                    <p className="mt-1 text-2xl font-bold tabular-nums sm:text-3xl">
                      {formatEsimPrice(pkg.price)}
                    </p>
                  </div>
                </div>
                <p className="mt-4 text-base font-semibold text-white/95">
                  {displayTitle}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold">
                    {t("esim.days", { count: pkg.durationDays })}
                  </span>
                  {pkg.activation ? (
                    <span className="rounded-full bg-white/15 px-3 py-1 text-xs font-semibold">
                      {activationLabel}
                    </span>
                  ) : null}
                </div>
              </div>

              <div className={`${panelClass} divide-y divide-border-soft !p-0`}>
                <SpecRow
                  label={t("esim.data_type")}
                  value={networksLabel}
                />
                <SpecRow
                  label={t("esim.valid_for")}
                  value={t("esim.days", { count: pkg.durationDays })}
                />
                <SpecRow
                  label={t("esim.activation")}
                  value={activationLabel}
                />
                <div className="flex flex-col gap-2 px-5 py-4 sm:flex-row sm:items-start sm:justify-between">
                  <span className="text-sm text-text-muted">
                    {t("esim.availability")}
                  </span>
                  <button
                    type="button"
                    disabled={coveredCountries.length === 0}
                    onClick={() => setCountriesOpen(true)}
                    className="group flex max-w-full flex-col items-start gap-1 text-start disabled:cursor-default sm:items-end"
                  >
                    <span className="inline-flex max-w-full items-center gap-2">
                      {previewFlags.map((c) => (
                        <img
                          key={c.code}
                          src={flagUrl(c.code)}
                          alt=""
                          width={22}
                          height={16}
                          className="h-4 w-[22px] rounded-sm object-cover ring-1 ring-border-soft"
                        />
                      ))}
                      {extraCount > 0 ? (
                        <span className="text-xs font-semibold text-text-muted">
                          +{extraCount}
                        </span>
                      ) : null}
                      <span className="truncate text-sm font-semibold text-text-heading">
                        {coverageLabel ||
                          (coveredCountries.length > 1
                            ? t("esim.covered_countries_count", {
                                count: coveredCountries.length,
                              })
                            : coveredCountries[0]?.name || "—")}
                      </span>
                      {coveredCountries.length > 0 ? (
                        <ChevronRight
                          className="h-4 w-4 shrink-0 text-text-muted"
                          aria-hidden
                        />
                      ) : null}
                    </span>
                    {coveredCountries.length > 1 ? (
                      <span className="text-xs font-semibold text-brand-500 group-hover:underline">
                        {t("esim.see_all_countries")}
                      </span>
                    ) : null}
                  </button>
                </div>
                {(pkg.carriers?.length || 0) > 0 ? (
                  <SpecRow
                    label={t("esim.carriers")}
                    value={`${pkg.carriers!.slice(0, 4).join(", ")}${
                      pkg.carriers!.length > 4
                        ? ` +${pkg.carriers!.length - 4}`
                        : ""
                    }`}
                  />
                ) : null}
                {pkg.withHotspot ||
                pkg.withDataRoaming ||
                pkg.withTopUp ||
                pkg.withUsageCheck ? (
                  <div className="flex flex-wrap gap-2 px-5 py-4">
                    {pkg.withHotspot ? (
                      <FeatureChip
                        icon={<Wifi className="h-3.5 w-3.5" aria-hidden />}
                        label={t("esim.chip_hotspot")}
                      />
                    ) : null}
                    {pkg.withDataRoaming ? (
                      <FeatureChip
                        icon={<Globe className="h-3.5 w-3.5" aria-hidden />}
                        label={t("esim.chip_roaming")}
                      />
                    ) : null}
                    {pkg.withTopUp ? (
                      <FeatureChip label={t("esim.chip_topup")} />
                    ) : null}
                    {pkg.withUsageCheck ? (
                      <FeatureChip
                        icon={
                          <RefreshCw className="h-3.5 w-3.5" aria-hidden />
                        }
                        label={t("esim.chip_usage_check")}
                      />
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className="rounded-2xl border border-brand-500/35 bg-[#FFF5F8] p-5 dark:bg-brand-500/10">
                <div className="flex items-center gap-2.5">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-brand-500/15 text-brand-500">
                    <Info className="h-4 w-4" aria-hidden />
                  </span>
                  <h2 className="text-sm font-bold text-brand-500">
                    {getFairUsageTitle(t)}
                  </h2>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-text-heading">
                  {getFairUsageBody(pkg, t)}
                </p>
              </div>

              <div className={`${cardClass} space-y-4 !shadow-none`}>
                <h2 className="text-sm font-bold uppercase tracking-wider text-text-muted">
                  {t("esim.why_esim")}
                </h2>
                <ul className="space-y-3">
                  {benefitItems.map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span
                        className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-500/15 text-brand-500"
                        aria-hidden
                      >
                        <Check className="h-3 w-3" strokeWidth={3} />
                      </span>
                      <span className="text-sm leading-snug text-text-heading">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>

              <button
                type="button"
                onClick={goCheckout}
                className="inline-flex w-full items-center justify-center rounded-full bg-brand-500 px-6 py-3.5 text-base font-bold text-white shadow-sm transition hover:bg-brand-600"
              >
                {t("esim.buy_now_cta", {
                  price: formatEsimPrice(pkg.price),
                })}
              </button>

              <p className="text-center text-xs text-text-muted">
                {t("esim.details_terms_hint")}
              </p>
            </div>
          ) : null}
        </article>
      </Container>

      {countriesOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/45 p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="esim-countries-title"
          onClick={() => setCountriesOpen(false)}
        >
          <div
            className="max-h-[80vh] w-full max-w-lg overflow-hidden rounded-2xl bg-bg-base shadow-xl dark:bg-bg-subtle"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-border-soft px-5 py-4">
              <h2
                id="esim-countries-title"
                className="text-base font-semibold text-text-heading"
              >
                {t("esim.covered_countries_title")}
              </h2>
              <button
                type="button"
                onClick={() => setCountriesOpen(false)}
                className="rounded-full px-3 py-1 text-sm font-semibold text-brand-500"
              >
                {t("esim.close")}
              </button>
            </div>
            <ul className="max-h-[60vh] overflow-y-auto px-2 py-2">
              {coveredCountries.map((c) => (
                <li
                  key={c.code}
                  className="flex items-center gap-3 rounded-xl px-3 py-2.5"
                >
                  <img
                    src={flagUrl(c.code)}
                    alt=""
                    width={28}
                    height={20}
                    className="h-5 w-7 rounded-sm object-cover ring-1 ring-border-soft"
                  />
                  <span className="flex-1 text-sm font-medium text-text-heading">
                    {c.name}
                  </span>
                  <span className="text-xs font-semibold uppercase text-text-muted">
                    {c.code}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </main>
  );
}

function SpecRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <span className="text-sm text-text-muted">{label}</span>
      <span className="text-sm font-semibold text-text-heading sm:text-end">
        {value}
      </span>
    </div>
  );
}

function FeatureChip({
  label,
  icon,
}: {
  label: string;
  icon?: ReactNode;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-500/10 px-2.5 py-1 text-xs font-semibold text-brand-500 dark:bg-brand-500/20">
      {icon}
      {label}
    </span>
  );
}
